import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import de from './locales/de';
import en from './locales/en';

/**
 * Localisation for the Provisioning Center.
 *
 * Language choice, in order:
 *   1. what the user picked here before (localStorage)
 *   2. the browser's preference
 *   3. English
 *
 * English is the fallback rather than German because the team is not
 * German-only and an unlocalised key should degrade into something everyone can
 * read. German browsers still land on German without doing anything.
 */

export const SUPPORTED_LANGUAGES = ['de', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, { label: string; native: string }> = {
  de: { label: 'German', native: 'Deutsch' },
  en: { label: 'English', native: 'English' },
};

export const STORAGE_KEY = 'juni-pc-language';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // One namespace holding the whole tree, so a dotted key like
    // "nav.dashboard" resolves as a *path* inside it. Declaring `common`, `nav`
    // etc. as separate namespaces looked tidier but broke every lookup:
    // i18next then searched for the literal key "nav.dashboard" inside the
    // default namespace and, finding nothing, rendered the key itself.
    resources: {
      de: { translation: de },
      en: { translation: en },
    },
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: 'en',
    // "de-AT" and "de-CH" should get German, not fall through to English.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    defaultNS: 'translation',
    // ':' would otherwise be read as a namespace separator, and '.' must stay
    // the key-path separator for the nested structure above.
    nsSeparator: false,
    keySeparator: '.',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escapes for us; double-escaping would show &amp; in the UI.
      escapeValue: false,
    },
    returnEmptyString: false,
  });

/** Applied to the <html lang> attribute so screen readers and spellcheck follow. */
export function applyDocumentLanguage(lng: string): void {
  document.documentElement.lang = lng.split('-')[0];
}

applyDocumentLanguage(i18n.resolvedLanguage ?? 'en');
i18n.on('languageChanged', applyDocumentLanguage);

export default i18n;
